<template>
  <div>
    <v-dialog :value="sectionEditor.show" max-width="1600px" persistent @click:outside="handleClosePopup">
      <div style="background-color: #fff">
        <div class="header-modal">
          <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
            <span style="color: #0081c7">{{ fields['title'] }}</span>
            <v-btn icon @click="handleClosePopup">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
        </div>
        <v-card-text>
          <div>
            <label class="label-input">Section Name</label>
            <v-row>
              <v-col cols="12" md="8">
                <div>
                  <v-text-field
                    v-model="fields['title']"
                    label="Name"
                    :rules="[v => (v !== null && v !== undefined && v !== '') || 'Title is required!']"
                  />
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <v-checkbox small v-model="fields['enableAnchor']" label="Enable Anchor" />
              </v-col>
            </v-row>
          </div>
          <div class="mb-6">
            <label class="label-input">Review Info</label>
            <div class="group-item">
              <div v-if="!isTypeAccordionSwiperReviews">
                <div class="mb-1 mt-4">Title</div>
                <ckeditor :editor="editor" v-model="fields['title-review']" :config="editorConfig" />
              </div>
              <v-checkbox
                class="mt-4"
                v-if="isTypeFastvaluedealReviews"
                v-model="fields['show-title-underline']"
                label="Show Title Underline"
                dense
                hide-details
              />
              <v-row class="mt-4">
                <v-col v-if="showAverageRatingField" cols="12" :md="showRecommendCountField ? 4 : 6">
                  <v-text-field
                    v-model.number="fields['average-rating']"
                    label="Average Rating ⭐"
                    type="number"
                    min="0.1"
                    :rules="[v => (v >= 0.1 && v <= 5) || 'Average rating must be between 0.1 and 5.0']"
                  />
                </v-col>
                <v-col v-if="showTotalReviewersField" cols="12" :md="showRecommendCountField ? 4 : 6">
                  <v-text-field
                    v-model.number="fields['total-reviewers']"
                    label="Total Reviewers"
                    type="number"
                    min="1"
                    inputmode="numeric"
                    :rules="[
                      v =>
                        (Number.isInteger(v) && v >= 1) ||
                        'Total reviewers must be greater than 1 & must be an integer only',
                    ]"
                  />
                </v-col>
                <v-col v-if="showRecommendCountField" cols="12" md="4">
                  <v-text-field
                    v-model.number="fields['recommend-count']"
                    label="Recommend Count"
                    type="number"
                    min="0"
                    inputmode="numeric"
                    :rules="[
                      v =>
                        (Number.isInteger(v) && v >= 1) ||
                        'Recommend count must be greater than 1 & must be an integer only',
                    ]"
                  />
                </v-col>
              </v-row>

              <div v-if="showRateDistributionField" class="mt-4">
                <div class="font-weight-medium pb-4">
                  Rate distribution depends on total reviewers ({{ fields['total-reviewers'] || 0 }})
                </div>
                <v-row>
                  <v-col v-for="rate in [5, 4, 3, 2, 1]" :key="rate" cols="12" md="2">
                    <v-text-field
                      :label="`${rate} ⭐`"
                      type="number"
                      min="0"
                      inputmode="numeric"
                      :value="(fields['rate-distribution'] || {})[`rate-${rate}`]"
                      @input="v => handleRateItemInput(rate, v)"
                      hide-details
                      dense
                    />
                  </v-col>
                </v-row>
                <div class="text-caption mt-2">
                  Total: {{ rateSum }} / {{ fields['total-reviewers'] || 0 }} (remaining: {{ rateRemaining }})
                </div>
              </div>
            </div>
          </div>
          <div v-if="!isTypeAccordionSwiperReviews">
            <label class="label-input">Load More</label>
            <div class="group-item">
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.number="fields['reviews-limit']"
                    label="Reviews Limit"
                    type="number"
                    min="1"
                    :rules="[
                      v =>
                        (Number.isInteger(v) && v >= 1 && v <= fields['body-reviews'].length) ||
                        'Reviews limit must be an integer between 1 and total reviews',
                    ]"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field v-model="fields['load-more-label']" label="Load More Button Label" />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    type="number"
                    v-model.number="fields.buttonBorderRadius"
                    label="Border Radius"
                    min="0"
                    suffix="px"
                    :rules="[v => v >= 0 || 'Border Radius must be greater than or equal 0']"
                  />
                </v-col>
              </v-row>
            </div>
          </div>
          <div v-if="isTypeProductReviews04" class="mt-6">
            <label class="label-input mb-2 d-flex align-center" htmlFor="productEnable" style="width: fit-content">
              <span>Product</span>
              <input class="ml-2 cursor-pointer" type="checkbox" v-model="fields.product.enabled" id="productEnable" />
            </label>
            <div class="group-item">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field v-model="fields.product.title" label="Product Title" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="fields.product.name" label="Product Name" />
                </v-col>
              </v-row>
              <UploadImage
                @callback="obj => handleUploadProductImage(obj)"
                :image="{ imageSrc: fields.product.image }"
                :height="`200`"
                :typeUpload="'image'"
                @callbackDelete="handleRemoveProductImage"
              />
            </div>
          </div>
          <div class="mt-6">
            <label class="label-input">Section Settings</label>
            <div class="group-item">
              <div class="mb-6">
                <div class="font-weight-medium">Spacing</div>
                <v-row>
                  <v-col cols="6">
                    <v-checkbox v-model="fields.topPadding" label="Top Spacing" dense hide-details />
                  </v-col>
                  <v-col cols="6">
                    <v-checkbox v-model="fields.bottomPadding" label="Bottom Spacing" dense hide-details />
                  </v-col>
                </v-row>
              </div>
              <div class="mb-6">
                <div class="font-weight-medium mb-3">Container & Card Colors</div>
                <v-row class="mb-2">
                  <v-col cols="12" md="3">
                    <div class="description-color-picker">
                      <input
                        type="color"
                        name="container-background"
                        class="description-color-picker__native"
                        :value="fields['container-background']"
                        @change="handleChangeContainerBackground"
                      />
                      <v-text-field
                        style="flex: 1"
                        v-model="fields['container-background']"
                        label="Background Color"
                        hide-details
                        dense
                        small
                      />
                      <v-btn @click="handleResetContainerBackground" small text color="primary"> Reset </v-btn>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="description-color-picker">
                      <input
                        type="color"
                        name="card-background"
                        class="description-color-picker__native"
                        :value="fields['card-background']"
                        @change="handleChangeCardBackground"
                      />
                      <v-text-field
                        style="flex: 1"
                        v-model="fields['card-background']"
                        label="Card Background Color"
                        hide-details
                        dense
                        small
                      />
                      <v-btn @click="handleResetCardBackground" small text color="primary"> Reset </v-btn>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="description-color-picker">
                      <input
                        type="color"
                        name="card-text-color"
                        class="description-color-picker__native"
                        :value="fields['card-text-color']"
                        @change="handleChangeCardTextColor"
                      />
                      <v-text-field
                        style="flex: 1"
                        v-model="fields['card-text-color']"
                        label="Card Text Color"
                        hide-details
                        dense
                        small
                      />
                      <v-btn @click="handleResetCardTextColor" small text color="primary"> Reset </v-btn>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="description-color-picker">
                      <input
                        type="color"
                        name="star-color"
                        class="description-color-picker__native"
                        :value="fields['star-color']"
                        @change="handleChangeStarColor"
                      />
                      <v-text-field
                        style="flex: 1"
                        v-model="fields['star-color']"
                        label="Star Color"
                        hide-details
                        dense
                        small
                      />
                      <v-btn @click="handleResetStarColor" small text color="primary"> Reset </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </div>
              <div>
                <div class="font-weight-medium mb-3">Button Colors</div>
                <v-row>
                  <v-col cols="12" md="4">
                    <div class="description-color-picker">
                      <input
                        type="color"
                        name="load-more-text-color"
                        class="description-color-picker__native"
                        :value="fields['load-more-text-color']"
                        @change="handleChangeLoadMoreTextColor"
                      />
                      <v-text-field
                        style="flex: 1"
                        v-model="fields['load-more-text-color']"
                        label="Button Text Color"
                        hide-details
                        dense
                        small
                      />
                      <v-btn @click="handleResetLoadMoreTextColor" small text color="primary"> Reset </v-btn>
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="description-color-picker">
                      <input
                        type="color"
                        name="load-more-background"
                        class="description-color-picker__native"
                        :value="fields['load-more-background']"
                        @change="handleChangeLoadMoreBackground"
                      />
                      <v-text-field
                        style="flex: 1"
                        v-model="fields['load-more-background']"
                        label="Button Background Color"
                        hide-details
                        dense
                        small
                      />
                      <v-btn @click="handleResetLoadMoreBackground" small text color="primary"> Reset </v-btn>
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="description-color-picker">
                      <input
                        type="color"
                        name="button-border-color"
                        class="description-color-picker__native"
                        :value="fields.buttonBorderColor"
                        @change="handleChangeButtonBorderColor"
                      />
                      <v-text-field
                        style="flex: 1"
                        v-model="fields.buttonBorderColor"
                        label="Button Border Color"
                        hide-details
                        dense
                        small
                      />
                      <v-btn @click="handleResetButtonBorderColor" small text color="primary"> Reset </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </div>
          </div>
          <div v-if="isTypeAccordionSwiperReviews" class="mt-6">
            <AccordionPrimary :accordion="fields.accordion" label="Accordion" />
          </div>
          <div v-if="isTypeFastvaluedealReviews || isTypeAccordionSwiperReviews" class="mt-6">
            <DividerPrimary :divider="fields.divider" label="Divider" />
          </div>
          <div class="reviews-header">
            <div class="reviews-header-left">
              <label class="label-input">Reviews ({{ fields['body-reviews'].length }})</label>
            </div>
            <div class="reviews-controls">
              <div v-if="selectedReviews.length > 0" class="selected-info">
                <span class="selected-count">{{ selectedReviews.length }} selected</span>
                <v-btn small text color="error" @click="handleRemoveSelectedReviews">
                  <v-icon size="16" class="mr-1">mdi-delete</v-icon>
                  Delete Selected
                </v-btn>
              </div>
              <div class="search-section">
                <v-text-field
                  v-model="searchReview"
                  label="Search reviews"
                  single-line
                  hide-details
                  dense
                  outlined
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  style="width: 280px; min-width: 280px"
                  @input="handleSearchReview"
                  placeholder="Search by name or title..."
                />
              </div>

              <div class="import-section">
                <v-btn color="primary" @click="showImportModal = true">
                  <v-icon size="16" class="mr-1">mdi-plus</v-icon>
                  <span>Add reviews</span>
                </v-btn>
              </div>
              <div class="export-section">
                <v-btn
                  :disabled="!fields['body-reviews'] || fields['body-reviews'].length === 0"
                  color="success"
                  @click="handleExportReviews"
                >
                  <v-icon size="16" class="mr-1">mdi-download</v-icon>
                  <span>Export reviews</span>
                </v-btn>
              </div>
            </div>
          </div>

          <div>
            <v-data-table
              :key="tableKey"
              :headers="reviewHeaders"
              :items="fields['body-reviews']"
              :items-per-page="10"
              class="elevation-1"
              :loading="false"
              :search="searchReview"
              :custom-filter="customFilter"
              v-model="selectedReviews"
              show-select
              item-key="id"
              :footer-props="{
                'items-per-page-options': [5, 10, 25, 50, 100],
                'items-per-page-text': 'Reviews per page',
              }"
            >
              <!-- Avatar Column -->
              <template slot="item.avatar" slot-scope="{ item }">
                <div class="reviewer-avatar">
                  <img :src="item.avatar" alt="" onerror="this.style.display='none'" />
                </div>
              </template>

              <!-- Reviewer Name Column -->
              <template slot="item.fullName" slot-scope="{ item }">
                <v-text-field
                  v-model="item.fullName"
                  dense
                  outlined
                  hide-details
                  placeholder="Reviewer name"
                  @blur="handleReviewUpdate"
                />
              </template>

              <!-- Rating Column -->
              <template slot="item.star" slot-scope="{ item }">
                <v-rating
                  color="warning"
                  background-color="#c1c1c1"
                  empty-icon="mdi-star-outline"
                  full-icon="mdi-star"
                  half-icon="mdi-star-half-full"
                  hover
                  length="5"
                  size="20"
                  v-model="item.star"
                  @change="handleReviewUpdate"
                />
              </template>

              <!-- Temporarily hide time, so hide date column -->
              <!-- <template slot="item.date" slot-scope="{ item }">
                <ReviewDateTimeInput :value="item" @input="handleDateTimeChange($event, item)" />
              </template> -->

              <!-- Title Column -->
              <template slot="item.title" slot-scope="{ item }">
                <v-text-field
                  v-model="item.title"
                  dense
                  outlined
                  hide-details
                  placeholder="Review title"
                  @blur="handleReviewUpdate"
                />
              </template>

              <!-- Images Count Column -->
              <template slot="item.images" slot-scope="{ item }">
                <div class="d-flex align-center">
                  <span class="mr-2">{{ item.images ? item.images.length : 0 }} images</span>
                  <v-btn icon small @click="handleAddReviewImage(fields['body-reviews'].indexOf(item))">
                    <v-icon size="20">mdi-image-plus</v-icon>
                  </v-btn>
                </div>
              </template>

              <!-- Content Column -->
              <template slot="item.content" slot-scope="{ item }">
                <div v-html="item.content" class="line-clamp-1"></div>
              </template>

              <!-- Actions Column -->
              <template slot="item.actions" slot-scope="{ item }">
                <div class="d-flex align-center justify-end">
                  <v-btn icon small @click="handleEditReview(fields['body-reviews'].indexOf(item))" class="mr-1">
                    <v-icon size="24" color="primary">mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon small @click="handleRemoveReview(fields['body-reviews'].indexOf(item))">
                    <v-icon size="24" color="error">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
        <div class="footer-modal">
          <v-card-actions style="padding: 10px 0">
            <v-spacer></v-spacer>
            <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
            <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
          </v-card-actions>
        </div>
      </div>
    </v-dialog>

    <!-- Review Image Upload Dialog -->
    <ReviewImageDialog
      :reviewImageDialog="reviewImageDialog"
      @update:reviewImageDialog="v => (reviewImageDialog = v)"
      :editingImageIndex="editingImageIndex"
      :currentReviewImage="currentReviewImage"
      @cancel-review-image="handleCancelReviewImageDialog"
      @review-image-uploaded="handleReviewImageUploaded"
      @remove-review-image-dialog="handleRemoveReviewImageDialog"
      @save-review-image="handleSaveReviewImage"
    />

    <!-- Review Edit Dialog -->
    <ReviewEditDialog
      :reviewEditDialog="reviewEditDialog"
      @update:reviewEditDialog="v => (reviewEditDialog = v)"
      :fields="fields"
      :editingReviewIndex="editingReviewIndex"
      :editor="editor"
      :editorConfig="editorConfig"
      @upload-avatar-from-dialog="handleUploadAvatarFromDialog"
      @remove-avatar-from-dialog="handleRemoveAvatarFromDialog"
      @add-review-image="handleAddReviewImage"
      @preview-image="handlePreviewImage"
      @edit-review-image="handleEditReviewImage"
      @remove-review-image="handleRemoveReviewImage"
      @cancel-review-edit="handleCancelReviewEdit"
      @save-review-edit="handleSaveReviewEdit"
    />

    <!-- Image Preview Dialog -->
    <ImagePreviewDialog
      :previewImageDialog="previewImageDialog"
      @update:previewImageDialog="v => (previewImageDialog = v)"
      :previewImageSrc="previewImageSrc"
      @close-preview-image="() => (previewImageDialog = false)"
    />

    <!-- CSV Import Modal -->
    <ImportReviewsDialog
      :showImportModal="showImportModal"
      @update:showImportModal="v => (showImportModal = v)"
      :importTab="importTab"
      :manualReview="manualReview"
      :editor="editor"
      :editorConfig="editorConfig"
      :randomReviewsCount="randomReviewsCount"
      :maxRandomReviewsCount="maxRandomReviewsCount"
      :selectedGender="selectedGender"
      :genderOptions="genderOptions"
      :formValid="formValid"
      :isDragging="isDragging"
      :csvFile="csvFile"
      :csvLoading="csvLoading"
      :csvError="csvError"
      :importedReviews="importedReviews"
      :duplicateCount="duplicateCount"
      @close-import-modal="handleCloseImportModal"
      @set-dragging="v => (isDragging = v)"
      @drop-file="handleDrop"
      @file-select="handleFileSelect"
      @remove-csv-file="handleRemoveCsvFile"
      @save-add-reviews="handleSaveAddReviews"
      @update:manualReview="v => (manualReview = v)"
      @update:importTab="v => (importTab = v)"
      @update:randomReviewsCount="v => (randomReviewsCount = v)"
      @update:selectedGender="v => (selectedGender = v)"
      @manual-add-image="handleManualAddImage"
      @manual-preview-image="handlePreviewImage"
      @manual-edit-image="handleManualEditImage"
    />
  </div>
</template>

<script>
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { cloneDeepObject } from '../../common/utils';
import { updateHtmlFromData } from '../../common/html/update';
import ReviewImageDialog from './review-image-dialog.vue';
import ReviewEditDialog from './review-edit-dialog.vue';
import ImagePreviewDialog from './image-preview-dialog.vue';
import ImportReviewsDialog from './import-reviews-dialog.vue';
// import ReviewDateTimeInput from './review-date-time-input.vue'
import productReviewsJson from '@/assets/json/product-reviews.json';
import { sectionType } from '@/views/website/products/components/description/common/types';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import DividerPrimary from '../common/divider-primary.vue';
import AccordionPrimary from '../common/accordion-primary.vue';

export default {
  name: 'ProductReviewsSectionEditor',
  components: {
    ReviewImageDialog,
    ReviewEditDialog,
    ImagePreviewDialog,
    ImportReviewsDialog,
    // ReviewDateTimeInput,
    UploadImage,
    DividerPrimary,
    AccordionPrimary,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      // Review image dialog states
      reviewImageDialog: false,
      reviewEditDialog: false,
      previewImageDialog: false,
      previewImageSrc: '',
      // Manual add review state
      manualReview: {
        id: null,
        fullName: '',
        avatar: '',
        star: 5,
        title: '',
        content: '',
        images: [],
        dateValue: new Date().toISOString().substr(0, 10),
        date: new Date().toLocaleString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
        }),
      },
      editingReviewIndex: null,
      editingImageIndex: null,
      currentReviewImage: {
        src: '',
      },
      // Data table properties
      searchReview: '',
      tableKey: 0,
      selectedReviews: [],
      reviewHeaders: [
        { text: '', value: 'data-table-select', sortable: false, width: '40px' },
        { text: 'Avatar', value: 'avatar', sortable: false, width: '40px' },
        { text: 'Reviewer Name', value: 'fullName', sortable: false, width: '150px' },
        { text: 'Title', value: 'title', sortable: false, width: '180px' },
        { text: 'Content', value: 'content', sortable: false, width: '180px' },
        // { text: 'Date', value: 'date', sortable: false, width: '200px' },
        { text: 'Rating', value: 'star', sortable: false, width: '100px' },
        { text: 'Images', value: 'images', sortable: false, width: '100px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '80px', align: 'end' },
      ],
      fields: {
        title: 'Product Reviews Section',
        'title-review': 'Reviews',
        'recommend-count': 2,
        'reviews-limit': 5,
        'load-more-label': 'Load more',
        'load-more-background': '#000000',
        'load-more-text-color': '#ffffff',
        'container-background': '#f3f4f6',
        'card-background': '#ffffff',
        'card-text-color': '#000000',
        'star-color': '#f0b100',
        'overview-background': '#ffffff',
        'average-rating': 5.0,
        'total-reviewers': 3,
        'body-reviews': [],
        topPadding: true,
        bottomPadding: true,
        product: {
          image: '',
          name: '',
          title: '',
          enabled: true,
        },
        buttonBorderColor: '#000000',
        buttonBorderRadius: 8,
        divider: {
          show: true,
          maxWidth: 200,
          height: 6,
          backgroundColor: '#5B04DE',
          align: 'center',
        },
        accordion: {
          open: true,
          title: '',
          backgroundColor: '#ffffff',
          color: '#000000',
          icon: {
            type: 'plus-minus',
            position: 'right',
            show: true,
          },
        },
      },
      // CSV Import properties
      csvFile: null,
      csvLoading: false,
      csvError: null,
      importedReviews: [],
      formValid: true,
      isDragging: false,
      showImportModal: false,
      importTab: 0,
      randomReviewsCount: 10,
      maxRandomReviewsCount: 10,
      selectedGender: 'Unisex',
      genderOptions: [
        { text: 'Unisex', value: 'Unisex' },
        { text: 'Male', value: 'Male' },
        { text: 'Female', value: 'Female' },
      ],
      duplicateCount: 0,
    };
  },
  computed: {
    productReviewTypeNormalized() {
      return (this.sectionEditor.section.type || '').trim();
    },
    isTypeProductReviews() {
      return this.productReviewTypeNormalized === sectionType.sectionProductReviews;
    },
    isTypeProductReviews02() {
      return this.productReviewTypeNormalized === sectionType.sectionProductReviews02;
    },
    isTypeProductReviews03() {
      return this.productReviewTypeNormalized === sectionType.sectionProductReviews03;
    },
    isTypeProductReviews04() {
      return this.productReviewTypeNormalized === sectionType.sectionProductReviews04;
    },
    isTypeProductReviews05() {
      return this.productReviewTypeNormalized === sectionType.sectionProductReviews05;
    },
    isTypeProductReviews06() {
      return this.productReviewTypeNormalized === sectionType.sectionProductReviews06;
    },
    isTypeGatorzReviews() {
      return this.productReviewTypeNormalized === sectionType.sectionGatorzReviews;
    },
    isTypeSaintbrandReviews() {
      return this.productReviewTypeNormalized === sectionType.sectionSaintbrandReviews;
    },
    isTypeLosaprilyReviews() {
      return this.productReviewTypeNormalized === sectionType.sectionLosaprilyReviews;
    },
    isTypeAccordionSwiperReviews() {
      return this.isTypeGatorzReviews;
    },
    isTypeFastvaluedealReviews() {
      return this.productReviewTypeNormalized === sectionType.sectionFastvaluedealReviews;
    },
    showRecommendCountField() {
      return this.isTypeProductReviews;
    },
    showTotalReviewersField() {
      return (
        this.isTypeProductReviews ||
        this.isTypeProductReviews02 ||
        this.isTypeProductReviews05 ||
        this.isTypeProductReviews06 ||
        this.isTypeAccordionSwiperReviews ||
        this.isTypeFastvaluedealReviews ||
        this.isTypeSaintbrandReviews ||
        this.isTypeLosaprilyReviews
      );
    },
    showAverageRatingField() {
      return (
        this.isTypeProductReviews ||
        this.isTypeProductReviews02 ||
        this.isTypeProductReviews05 ||
        this.isTypeProductReviews06 ||
        this.isTypeAccordionSwiperReviews ||
        this.isTypeFastvaluedealReviews ||
        this.isTypeSaintbrandReviews ||
        this.isTypeLosaprilyReviews
      );
    },
    showRateDistributionField() {
      return (
        this.isTypeProductReviews06 ||
        this.isTypeAccordionSwiperReviews ||
        this.isTypeFastvaluedealReviews ||
        this.isTypeSaintbrandReviews ||
        this.isTypeLosaprilyReviews
      );
    },
    rateSum() {
      if (!this.showRateDistributionField) return 0;
      const dist = this.fields['rate-distribution'] || {};
      return [1, 2, 3, 4, 5].reduce((sum, r) => sum + (parseInt(dist[`rate-${r}`]) || 0), 0);
    },
    rateRemaining() {
      if (!this.showRateDistributionField) return 0;
      const total = parseInt(this.fields['total-reviewers']) || 0;
      return Math.max(0, total - this.rateSum);
    },
  },
  methods: {
    ensureAccordionField() {
      if (!this.isTypeAccordionSwiperReviews) return;
      if (!this.fields.accordion || typeof this.fields.accordion !== 'object') {
        this.$set(this.fields, 'accordion', {
          open: true,
          title: '',
          backgroundColor: '#ffffff',
          color: '#000000',
          icon: {
            type: 'plus-minus',
            position: 'right',
            show: true,
          },
        });
        return;
      }

      const defaultIcon = {
        type: 'plus-minus',
        position: 'right',
        show: true,
      };
      this.fields.accordion = {
        open: this.fields.accordion.open !== false,
        title: this.fields.accordion.title || '',
        backgroundColor: this.fields.accordion.backgroundColor || '#ffffff',
        color: this.fields.accordion.color || '#000000',
        icon: {
          ...defaultIcon,
          ...(this.fields.accordion.icon || {}),
        },
      };
    },
    ensureProductField() {
      if (!this.fields.product || typeof this.fields.product !== 'object') {
        this.$set(this.fields, 'product', {
          image: '',
          name: '',
          title: '',
          enabled: true,
        });
        return;
      }

      this.fields.product = {
        image: this.fields.product.image || '',
        name: this.fields.product.name || '',
        title: this.fields.product.title || '',
        enabled: this.fields.product.enabled !== false,
      };
    },
    ensureRateFields() {
      if (!this.showRateDistributionField) return;
      const total = parseInt(this.fields['total-reviewers']) || 0;
      if (!this.fields['rate-distribution'] || typeof this.fields['rate-distribution'] !== 'object') {
        this.$set(this.fields, 'rate-distribution', {
          'rate-5': total,
          'rate-4': 0,
          'rate-3': 0,
          'rate-2': 0,
          'rate-1': 0,
        });
      }
      const dist = this.fields['rate-distribution'];
      [1, 2, 3, 4, 5].forEach(r => {
        const key = `rate-${r}`;
        const v = parseInt(dist[key]);
        this.$set(dist, key, Number.isFinite(v) && v >= 0 ? v : 0);
      });

      // Ensure sum <= total by clamping rate-5 first
      const sum = [1, 2, 3, 4, 5].reduce((s, r) => s + (parseInt(dist[`rate-${r}`]) || 0), 0);
      if (total > 0 && sum > total) {
        const overflow = sum - total;
        const v5 = parseInt(dist['rate-5']) || 0;
        this.$set(dist, 'rate-5', Math.max(0, v5 - overflow));
      }
    },
    handleRateItemInput(rate, rawValue) {
      if (!this.showRateDistributionField) return;
      const total = parseInt(this.fields['total-reviewers']) || 0;
      this.ensureRateFields();
      const dist = this.fields['rate-distribution'];
      const key = `rate-${rate}`;
      const nextVal = Math.max(0, parseInt(rawValue) || 0);

      const otherSum = [1, 2, 3, 4, 5].reduce((s, r) => {
        if (r === rate) return s;
        return s + (parseInt(dist[`rate-${r}`]) || 0);
      }, 0);
      const maxAllowed = Math.max(0, total - otherSum);
      this.$set(dist, key, Math.min(nextVal, maxAllowed));
    },
    handleUploadProductImage(obj) {
      if (obj && obj.imageSrc) {
        this.fields.product.image = obj.imageSrc;
      }
    },
    handleRemoveProductImage() {
      this.fields.product.image = '';
    },
    handleManualAddImage() {
      // Reuse review image dialog for manual form
      this.currentReviewImage = { src: '' };
      this.editingReviewIndex = null; // manual mode
      this.editingImageIndex = null;
      this.reviewImageDialog = true;
    },
    handleManualEditImage(imageIndex) {
      if (this.manualReview.images && this.manualReview.images[imageIndex]) {
        this.currentReviewImage = { src: this.manualReview.images[imageIndex].src };
        this.editingReviewIndex = null;
        this.editingImageIndex = imageIndex;
        this.reviewImageDialog = true;
      }
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updatedSection = {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtmlFromData.sectionProductReviews(
          this.sectionEditor.section.html,
          this.fields,
          this.sectionEditor.section.type,
        ),
      };
      this.$emit('update:section', updatedSection);
      this.handleClosePopup();
    },
    formatDateToExport(dateInput) {
      // Convert various date inputs to desired export format like:
      // Tue Aug 17 2025 11:30:31 GMT+0700 (Indochina Time)
      let parsed;
      if (!dateInput) {
        parsed = new Date();
      } else if (dateInput instanceof Date) {
        parsed = dateInput;
      } else {
        parsed = new Date(dateInput);
      }
      if (isNaN(parsed.getTime())) {
        parsed = new Date();
      }
      return parsed.toString();
    },
    formatDateToISO(date) {
      if (!date) return new Date().toISOString().substr(0, 10);
      const parsedDate = new Date(date);
      // Check if the date is valid
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toISOString().substr(0, 10);
      } else {
        // Fallback to current date if parsing fails
        return new Date().toISOString().substr(0, 10);
      }
    },
    formatDateToDisplay(date) {
      if (!date) date = new Date();

      // If it's already a Date object, use it directly
      let parsedDate;
      if (date instanceof Date) {
        parsedDate = date;
      } else {
        parsedDate = new Date(date);
      }

      // Check if the date is valid
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toLocaleString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
        });
      } else {
        // Fallback to current date if parsing fails
        return new Date().toLocaleString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
        });
      }
    },
    formatDateDisplay(dateValue) {
      if (!dateValue) return '';
      const parsedDate = new Date(dateValue);
      // Check if the date is valid
      if (!isNaN(parsedDate.getTime())) {
        return this.formatDateToDisplay(parsedDate);
      } else {
        return this.formatDateToDisplay(new Date());
      }
    },
    handleDateTimeChange(nextItem, originalItem) {
      Object.assign(originalItem, {
        dateValue: nextItem.dateValue,
        timeValue: nextItem.timeValue,
        date: nextItem.date,
        _iso: nextItem._iso,
      });
      this.sortReviewsByDate();
      this.calculateRatingSummary();
    },
    handleDateChange(item) {
      if (item.dateValue) {
        const parsedDate = new Date(item.dateValue);
        if (!isNaN(parsedDate.getTime())) {
          item.date = this.formatDateToDisplay(parsedDate);
        } else {
          item.date = this.formatDateToDisplay(new Date());
        }
      } else {
        item.date = this.formatDateToDisplay(new Date());
      }
      this.sortReviewsByDate();
      this.calculateRatingSummary();
    },
    sortReviewsByDate() {
      // const format = 'MMMM D, YYYY [at] hh:mm:ss A'
      // this.fields['body-reviews'].sort((a, b) => {
      //   const dateA = moment(a.date, format, true)
      //   const dateB = moment(b.date, format, true)
      //   // Handle invalid dates
      //   if (!dateA.isValid()) return 1
      //   if (!dateB.isValid()) return -1
      //   if (this.sortByDate === 'newest-first') {
      //     return dateB.valueOf() - dateA.valueOf() // Newest first
      //   } else if (this.sortByDate === 'oldest-first') {
      //     return dateA.valueOf() - dateB.valueOf() // Oldest first
      //   }
      // })
    },
    calculateRatingSummary() {
      // TODO: If total-reviewers not set, calculate average rating and total reviewers
      // const reviews = this.fields['body-reviews']
      // if (reviews.length > 0) {
      //   const averageRating = reviews.reduce((sum, review) => sum + (review.star || 5), 0) / reviews.length
      //   this.fields['average-rating'] = parseFloat(averageRating.toFixed(1))
      //   this.fields['total-reviewers'] = reviews.length
      // } else {
      //   this.fields['average-rating'] = 5.0
      //   this.fields['total-reviewers'] = 0
      // }
    },
    handleSearchReview() {
      // Search functionality is handled by v-data-table's built-in search
      // This method can be used for additional search logic if needed
    },

    handleRemoveReview(index) {
      this.fields['body-reviews'].splice(index, 1);
      this.calculateRatingSummary();
      // Clear selection if the removed item was selected
      this.selectedReviews = this.selectedReviews.filter(item => item !== this.fields['body-reviews'][index]);
    },
    handleRemoveSelectedReviews() {
      if (this.selectedReviews.length === 0) return;

      // Show confirmation alert
      const confirmMessage = `Are you sure you want to delete ${this.selectedReviews.length} selected review${
        this.selectedReviews.length > 1 ? 's' : ''
      }?`;
      const isConfirmed = confirm(confirmMessage);

      if (!isConfirmed) return;

      // Get IDs of selected reviews
      const selectedIds = this.selectedReviews.map(item => item.id);

      // Remove reviews by filtering out selected ones
      this.fields['body-reviews'] = this.fields['body-reviews'].filter(review => !selectedIds.includes(review.id));

      // Clear selection
      this.selectedReviews = [];
      this.calculateRatingSummary();

      // Force update to ensure table reflects changes
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    handleUploadAvatar(obj, index) {
      console.log('handleUploadAvatar called:', obj, index);
      if (obj && obj.imageSrc) {
        this.$set(this.fields['body-reviews'][index], 'avatar', obj.imageSrc);
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      }
    },
    handleRemoveAvatar(index) {
      this.$set(this.fields['body-reviews'][index], 'avatar', '');
    },
    // Methods for dialog upload
    handleUploadAvatarFromDialog(obj) {
      if (obj && obj.imageSrc && this.editingReviewIndex !== null) {
        this.$set(this.fields['body-reviews'][this.editingReviewIndex], 'avatar', obj.imageSrc);
        this.refreshTableData();
      }
    },
    handleRemoveAvatarFromDialog() {
      if (this.editingReviewIndex !== null) {
        this.$set(this.fields['body-reviews'][this.editingReviewIndex], 'avatar', '');
        this.$nextTick(() => {
          this.$forceUpdate();
        });
        console.log('Avatar removed from dialog for index:', this.editingReviewIndex);
      }
    },
    handleChangeContainerBackground(event) {
      this.fields['container-background'] = event.target.value;
    },
    handleResetContainerBackground() {
      console.log('handleResetContainerBackground', this.sectionEditor.section.type);
      switch (this.sectionEditor.section.type) {
        case sectionType.sectionProductReviews:
          this.fields['container-background'] = '#f3f4f6';
          break;
        case sectionType.sectionProductReviews04:
          this.fields['container-background'] = '#f7f7f7';
          break;

        default:
          this.fields['container-background'] = '#ffffff';
          break;
      }
    },
    handleChangeCardBackground(event) {
      this.fields['card-background'] = event.target.value;
      this.fields['overview-background'] = event.target.value;
    },
    handleResetCardBackground() {
      switch (this.sectionEditor.section.type) {
        default:
          this.fields['card-background'] = '#ffffff';
          this.fields['overview-background'] = '#ffffff';
          break;
      }
    },
    handleChangeStarColor(event) {
      this.fields['star-color'] = event.target.value;
    },
    handleResetStarColor() {
      this.fields['star-color'] = '#f0b100';
    },
    handleChangeLoadMoreBackground(event) {
      this.fields['load-more-background'] = event.target.value;
    },
    handleResetLoadMoreBackground() {
      switch (this.sectionEditor.section.type) {
        case sectionType.sectionProductReviews04:
          this.fields['load-more-background'] = '#ffffff';
          break;
        case sectionType.sectionProductReviews05:
          this.fields['load-more-background'] = '#ffffff';
          break;
        case sectionType.sectionProductReviews06:
        case sectionType.sectionGatorzReviews:
        case sectionType.sectionSaintbrandReviews:
        case sectionType.sectionLosaprilyReviews:
          this.fields['load-more-background'] = '#ffffff';
          break;
        case sectionType.sectionFastvaluedealReviews:
          this.fields['load-more-background'] = '#ffffff';
          break;
        default:
          this.fields['load-more-background'] = '#000000';
          break;
      }
    },
    handleChangeLoadMoreTextColor(event) {
      this.fields['load-more-text-color'] = event.target.value;
    },
    handleResetLoadMoreTextColor() {
      switch (this.sectionEditor.section.type) {
        case sectionType.sectionProductReviews04:
          this.fields['load-more-text-color'] = '#000000';
          break;
        case sectionType.sectionProductReviews05:
          this.fields['load-more-text-color'] = '#000000';
          break;
        case sectionType.sectionProductReviews06:
        case sectionType.sectionGatorzReviews:
        case sectionType.sectionSaintbrandReviews:
        case sectionType.sectionLosaprilyReviews:
          this.fields['load-more-text-color'] = '#000000';
          break;
        case sectionType.sectionFastvaluedealReviews:
          this.fields['load-more-text-color'] = '#000000';
          break;
        default:
          this.fields['load-more-text-color'] = '#ffffff';
          break;
      }
    },
    handleChangeCardTextColor(event) {
      this.fields['card-text-color'] = event.target.value;
    },
    handleResetCardTextColor() {
      this.fields['card-text-color'] = '#000000';
    },
    handleChangeButtonBorderColor(event) {
      this.fields['button-border-color'] = event.target.value;
    },
    handleResetButtonBorderColor() {
      this.fields['button-border-color'] = '#000000';
    },
    // Review image methods
    handleAddReviewImage(reviewIndex) {
      this.currentReviewImage = {
        src: '',
      };
      this.editingReviewIndex = reviewIndex;
      this.editingImageIndex = null;
      this.reviewImageDialog = true;
    },
    handleEditReviewImage(reviewIndex, imageIndex) {
      const review = this.fields['body-reviews'][reviewIndex];
      if (review.images && review.images[imageIndex]) {
        const image = review.images[imageIndex];
        this.currentReviewImage = {
          src: image.src,
        };
        this.editingReviewIndex = reviewIndex;
        this.editingImageIndex = imageIndex;
        this.reviewImageDialog = true;
      }
    },
    handleRemoveReviewImage(reviewIndex, imageIndex) {
      const review = this.fields['body-reviews'][reviewIndex];
      if (review.images) {
        review.images.splice(imageIndex, 1);
      }
    },
    handleReviewImageUploaded(obj) {
      if (obj && obj.imageSrc) {
        this.currentReviewImage.src = obj.imageSrc;
      }
    },
    handleRemoveReviewImageDialog() {
      this.currentReviewImage.src = '';
    },
    handleSaveReviewImage() {
      if (!this.currentReviewImage.src) return;

      const imageData = { src: this.currentReviewImage.src };

      if (this.editingReviewIndex === null) {
        // Manual form mode
        if (!this.manualReview.images) this.manualReview.images = [];
        if (this.editingImageIndex !== null) {
          this.$set(this.manualReview.images, this.editingImageIndex, imageData);
        } else {
          this.manualReview.images.push(imageData);
        }
      } else {
        // Editing existing review mode
        const review = this.fields['body-reviews'][this.editingReviewIndex];
        if (!review.images) {
          review.images = [];
        }
        if (this.editingImageIndex !== null) {
          review.images.splice(this.editingImageIndex, 1, imageData);
        } else {
          review.images.push(imageData);
        }
      }

      this.reviewImageDialog = false;
      this.currentReviewImage = { src: '' };
      this.editingImageIndex = null;
      this.$nextTick(() => this.$forceUpdate());
    },
    handleCancelReviewImageDialog() {
      this.reviewImageDialog = false;
      this.currentReviewImage = {
        src: '',
      };
      this.editingImageIndex = null;
      // Only reset editingReviewIndex if we're not in edit review dialog
      if (!this.reviewEditDialog) {
        this.editingReviewIndex = null;
      }
    },
    handlePreviewImage(src) {
      this.previewImageSrc = src;
      this.previewImageDialog = true;
    },
    // Data table methods
    customFilter(value, search, item) {
      return (
        item.fullName.toLowerCase().includes(search.toLowerCase()) ||
        item.title.toLowerCase().includes(search.toLowerCase())
      );
    },
    handleReviewUpdate() {
      this.calculateRatingSummary();
    },
    handleEditReview(index) {
      // Open edit dialog for detailed editing
      this.editingReviewIndex = index;
      this.reviewEditDialog = true;
    },
    handleSaveReviewEdit() {
      this.reviewEditDialog = false;
      this.editingReviewIndex = null;
      this.calculateRatingSummary();
      // Force update to ensure table reflects changes
      this.$forceUpdate();
    },
    handleCancelReviewEdit() {
      this.reviewEditDialog = false;
      this.editingReviewIndex = null;
    },
    // Method to refresh table data
    refreshTableData() {
      // Increment table key to force complete re-render
      this.tableKey++;
      // Create a new array reference to force table re-render
      const reviews = [...this.fields['body-reviews']];
      this.$set(this.fields, 'body-reviews', reviews);
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    // CSV Import methods
    handleDrop(e) {
      this.isDragging = false;
      const file = e.dataTransfer.files[0];
      this.validateAndImportFile(file);
    },
    handleFileSelect(e) {
      const file = e.target.files[0];
      this.validateAndImportFile(file);
      e.target.value = null;
    },
    handleRemoveCsvFile() {
      this.csvFile = null;
      this.csvError = '';
      this.importedReviews = [];
      this.duplicateCount = 0;
      if (this.$refs.csvInput) {
        this.$refs.csvInput.value = null;
      }
    },
    validateAndImportFile(file) {
      if (!file) return;
      if (!file.name.endsWith('.csv')) {
        this.csvError = 'File must be a .csv';
        this.csvFile = null;
        return;
      }
      this.csvError = '';
      this.csvFile = file;
      this.importCsv(file);
    },
    async importCsv(file) {
      return new Promise(resolve => {
        const reader = new FileReader();
        this.csvLoading = true;
        this.csvError = '';
        this.importedReviews = [];

        reader.onload = e => {
          try {
            let text = e.target.result.replace(/^\uFEFF/, '');
            const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
            if (lines.length < 2) {
              this.csvError = 'CSV file must have a header and at least one row.';
              this.csvLoading = false;
              resolve(false);
              return;
            }

            const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
            const requiredHeaders = ['name', 'avatar', 'rating', 'title', 'content', 'date'];
            const missing = requiredHeaders.filter(h => !headers.includes(h));
            if (missing.length > 0) {
              this.csvError = `Missing required columns: <span style="text-transform: capitalize;">${missing.join(
                ', ',
              )}</span>`;
              this.csvLoading = false;
              resolve(false);
              return;
            }

            const imageCols = {};
            headers.forEach((h, i) => {
              const match = h.match(/^image(\d+)$/i);
              if (match) imageCols[parseInt(match[1], 10)] = i;
            });

            const rows = lines.slice(1);
            const chunkSize = 200;
            const processChunk = async start => {
              const end = Math.min(start + chunkSize, rows.length);

              for (let i = start; i < end; i++) {
                const cols = this.parseCsvLine(rows[i]);
                const row = {};
                headers.forEach((h, idx) => (row[h] = cols[idx] ? cols[idx].trim() : ''));

                let rating = parseInt(row.rating, 10);
                if (isNaN(rating) || rating < 1 || rating > 5) rating = 5;
                let avatar = this.isValidUrl(row.avatar) ? row.avatar : '';
                const images = Object.keys(imageCols)
                  .sort((a, b) => a - b)
                  .map(idx => {
                    const url = cols[imageCols[idx]];
                    return url && this.isValidUrl(url) ? { src: url, alt: '' } : null;
                  })
                  .filter(Boolean);

                let finalDate = new Date(row.date);
                if (isNaN(finalDate.getTime())) finalDate = new Date();

                this.importedReviews.push({
                  id: this.fields['body-reviews'].length + this.importedReviews.length + 1,
                  fullName: row.name || '',
                  avatar,
                  star: rating,
                  title: row.title || '',
                  content: row.content || '',
                  images,
                  dateValue: this.formatDateToISO(finalDate),
                  date: this.formatDateToDisplay(finalDate),
                });
              }

              // wait for Vue update DOM
              await this.$nextTick();
              await new Promise(r => setTimeout(r, 0));

              if (end < rows.length) {
                await processChunk(end);
              } else {
                this.csvLoading = false;
                resolve(true);
              }
            };

            processChunk(0);

            // Auto-update total reviews count
            this.fields['total-reviewers'] = this.importedReviews.length;
            // Calculate duplicates within imported file (for tab v2 display)
            this.calculateDuplicateCount();
          } catch (err) {
            this.csvError = 'CSV parse error: ' + err.message;
            this.csvLoading = false;
            resolve(false);
          }
        };

        reader.onerror = () => {
          this.csvError = 'Failed to read file.';
          this.csvLoading = false;
          resolve(false);
        };
        reader.readAsText(file);
      });
    },
    parseCsvLine(line) {
      const result = [];
      let current = '';
      let insideQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"' && line[i + 1] === '"') {
          current += '"';
          i++;
        } else if (char === '"') {
          insideQuotes = !insideQuotes;
        } else if (char === ',' && !insideQuotes) {
          result.push(current);
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current);

      return result;
    },
    isValidUrl(url) {
      try {
        const u = new URL(url);
        return u.protocol === 'http:' || u.protocol === 'https:';
      } catch (_) {
        return false;
      }
    },

    // Calculate duplicates within importedReviews list (fullName + avatar)
    calculateDuplicateCount() {
      const seen = new Set();
      let dup = 0;
      for (const r of this.importedReviews) {
        const key = `${r.fullName || ''}|||${r.avatar || ''}`;
        if (seen.has(key)) dup++;
        else seen.add(key);
      }
      this.duplicateCount = dup;
    },

    handleCloseImportModal() {
      this.showImportModal = false;
      this.handleRemoveCsvFile();
      this.importTab = 0;
      this.randomReviewsCount = 10;
      this.selectedGender = 'Unisex';
      this.csvError = '';
      this.csvFile = null;
      this.importedReviews = [];
      this.csvLoading = false;
      this.isDragging = false;
    },
    handleSaveAddReviews() {
      if (this.importTab === 0) {
        // Manual add
        const mv = this.manualReview;
        if (!mv || !mv.fullName) return;
        const newItem = {
          id: (this.fields['body-reviews']?.length || 0) + 1,
          fullName: mv.fullName || '',
          avatar: mv.avatar || '',
          star: mv.star || 5,
          title: mv.title || '',
          content: mv.content || '',
          images: (mv.images || []).map(i => ({ src: i.src })),
          dateValue: mv.dateValue || this.formatDateToISO(new Date()),
          date: mv.date || this.formatDateToDisplay(new Date()),
        };
        this.fields['body-reviews'] = [newItem, ...(this.fields['body-reviews'] || [])];
        // reset manual form
        this.manualReview = {
          id: null,
          fullName: '',
          avatar: '',
          star: 5,
          title: '',
          content: '',
          images: [],
          dateValue: new Date().toISOString().substr(0, 10),
          date: this.formatDateToDisplay(new Date()),
        };
        this.calculateRatingSummary();
        this.$nextTick(() => this.$forceUpdate());
        this.handleCloseImportModal();
      } else if (this.importTab === 1) {
        // Random
        // Generate random reviews
        if (
          !this.randomReviewsCount ||
          this.randomReviewsCount < 1 ||
          this.randomReviewsCount > this.maxRandomReviewsCount
        ) {
          return;
        }
        // Build a set of existing keys: fullName + avatar
        const existingKeySet = new Set(
          (this.fields['body-reviews'] || []).map(r => `${r.fullName || ''}|||${r.avatar || ''}`),
        );

        // Filter reviews by gender first
        let genderFilteredReviews = productReviewsJson;
        if (this.selectedGender !== 'Unisex') {
          genderFilteredReviews = productReviewsJson.filter(review => review.gender === this.selectedGender);
        }

        // Filter unique candidates from gender-filtered JSON that are not in existing
        const uniqueCandidates = genderFilteredReviews.filter(r => {
          const key = `${r.fullName || ''}|||${r.avatar || ''}`;
          return !existingKeySet.has(key);
        });

        // Shuffle candidates to keep randomness but ensure uniqueness
        const shuffled = uniqueCandidates
          .map(v => ({ v, sort: Math.random() }))
          .sort((a, b) => a.sort - b.sort)
          .map(({ v }) => v);

        const toAddCount = Math.min(this.randomReviewsCount, shuffled.length);
        const duplicateCount = Math.max(0, this.randomReviewsCount - toAddCount);

        let offsetDays = 0;
        for (let i = 0; i < toAddCount; i++) {
          const item = shuffled[i];
          // Push offset date random (1-7)
          offsetDays += Math.floor(Math.random() * 7) + 1;
          const pastDate = new Date();
          pastDate.setDate(pastDate.getDate() - offsetDays);
          pastDate.setHours(Math.floor(Math.random() * 24));
          pastDate.setMinutes(Math.floor(Math.random() * 60));
          pastDate.setSeconds(Math.floor(Math.random() * 60));

          this.fields['body-reviews'].unshift({
            fullName: item.fullName || ``,
            date: this.formatDateToDisplay(pastDate),
            dateValue: this.formatDateToISO(pastDate),
            star: 5,
            title: item.title || '',
            avatar: item.avatar || '',
            content: item.content || '',
            images: [],
          });
        }

        // Show success message with counts
        if (toAddCount > 0) {
          let message = `Successfully added ${toAddCount} review${toAddCount > 1 ? 's' : ''}`;
          if (duplicateCount > 0) {
            message += `. ${duplicateCount} duplicate${duplicateCount > 1 ? 's' : ''} found and skipped`;
          }
          if (toAddCount < this.randomReviewsCount) {
            message += `. Could not add all ${this.randomReviewsCount} reviews due to duplicates`;
          }
          this.$store.dispatch('setMessage', { color: 'primary', dialog: true, content: message });
        } else if (duplicateCount > 0) {
          this.$store.dispatch('setMessage', {
            color: 'warning',
            dialog: true,
            content: `${duplicateCount} duplicate${duplicateCount > 1 ? 's' : ''} found. No new reviews added.`,
          });
        }

        this.calculateRatingSummary();
        this.$nextTick(() => {
          this.$forceUpdate();
        });
        this.handleCloseImportModal();
      } else if (this.importTab === 2) {
        // Import from CSV
        if (!this.importedReviews.length) {
          this.csvError = 'Please upload a valid CSV file before saving.';
          return;
        }

        const seenInFile = new Set();
        const dedupImported = [];
        for (const r of this.importedReviews) {
          const key = `${r.fullName || ''}|||${r.avatar || ''}`;
          if (!seenInFile.has(key)) {
            seenInFile.add(key);
            dedupImported.push(r);
          }
        }

        const existing = this.fields['body-reviews'] || [];
        const uniqueImported = dedupImported.filter(
          r => !existing.some(e => (e.fullName || '') === (r.fullName || '') && (e.avatar || '') === (r.avatar || '')),
        );

        const skippedAgainstExisting = dedupImported.length - uniqueImported.length;
        const totalDuplicates = (this.duplicateCount || 0) + skippedAgainstExisting;

        this.fields['body-reviews'] = [...uniqueImported, ...existing];

        if (uniqueImported.length > 0) {
          let message = `Successfully imported ${uniqueImported.length} review${uniqueImported.length > 1 ? 's' : ''}`;
          if (totalDuplicates > 0) {
            message += `. ${totalDuplicates} duplicate${totalDuplicates > 1 ? 's' : ''} found and skipped`;
          }
          this.$store.dispatch('setMessage', { color: 'primary', dialog: true, content: message });
        } else {
          this.$store.dispatch('setMessage', {
            color: 'warning',
            dialog: true,
            content: 'All imported reviews are duplicates. No new reviews imported.',
          });
        }

        this.calculateRatingSummary();
        this.selectedReviews = []; // Clear selected reviews after import
        this.handleCloseImportModal();
        this.$forceUpdate();
      }
    },
    handleExportReviews() {
      if (!this.fields['body-reviews'] || this.fields['body-reviews'].length === 0) {
        return;
      }

      // Find the maximum number of images across all reviews
      const maxImages = Math.max(
        ...this.fields['body-reviews'].map(review => (review.images ? review.images.length : 0)),
        0,
      );

      // Prepare CSV data with dynamic image columns
      const csvData = [];

      // Create header row with dynamic image columns
      const headers = ['Id', 'Name', 'Avatar', 'Rating', 'Title', 'Content', 'Date'];
      for (let i = 1; i <= maxImages; i++) {
        headers.push(`Image${i}`);
      }
      csvData.push(headers);

      console.log(this.fields['body-reviews']);

      // Add data rows
      this.fields['body-reviews'].forEach((review, index) => {
        const row = [
          index + 1, // Id
          review.fullName || '', // Name
          review.avatar || '', // Avatar
          review.star || 5, // Rating
          review.title || '', // Title
          review.content || '', // Content
          this.formatDateToExport(review.date) || '', // Date
        ];

        // Add image URLs based on actual number of images
        for (let i = 0; i < maxImages; i++) {
          if (review.images && review.images[i]) {
            row.push(review.images[i].src || '');
          } else {
            row.push(''); // Empty cell for missing images
          }
        }

        csvData.push(row);
      });

      // Convert to CSV string
      const csvContent = csvData
        .map(row =>
          row
            .map(cell => {
              // Escape quotes and wrap in quotes if contains comma, quote, or newline
              const escaped = String(cell).replace(/"/g, '""');
              if (escaped.includes(',') || escaped.includes('"') || escaped.includes('\n')) {
                return `"${escaped}"`;
              }
              return escaped;
            })
            .join(','),
        )
        .join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `product-reviews-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    updateMaxRandomReviewsCount() {
      // Filter reviews by gender if not Unisex
      let filteredReviews = productReviewsJson;
      if (this.selectedGender !== 'Unisex') {
        filteredReviews = productReviewsJson.filter(review => review.gender === this.selectedGender);
      }
      this.maxRandomReviewsCount = filteredReviews.length;
    },
  },
  mounted() {
    if (this.sectionEditor.sectionExtract) {
      this.fields = cloneDeepObject(this.sectionEditor.sectionExtract);
      this.ensureProductField();
      this.ensureAccordionField();
      this.ensureRateFields();

      // Ensure each review has required properties
      this.fields['body-reviews'].forEach((review, index) => {
        // Add id if missing
        if (!review.id) {
          review.id = index + 1;
        }

        if (!review.dateValue && review.date) {
          // Parse the existing date string to Date object then convert to ISO
          const parsedDate = new Date(review.date);
          if (!isNaN(parsedDate.getTime())) {
            review.dateValue = this.formatDateToISO(parsedDate);
          } else {
            // Fallback to current date if parsing fails
            review.dateValue = this.formatDateToISO(new Date());
          }
        } else if (!review.dateValue) {
          // No existing date, use current date
          review.dateValue = this.formatDateToISO(new Date());
        }
        if (!review.images) {
          review.images = [];
        }
      });
      // Sort reviews by date (newest first)
      this.sortReviewsByDate();
      this.calculateRatingSummary();
      this.updateMaxRandomReviewsCount();
    }
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal.sectionExtract) {
          this.fields = cloneDeepObject(newVal.sectionExtract);
          this.ensureProductField();
          this.ensureAccordionField();
          this.ensureRateFields();

          // Ensure each review has required properties
          this.fields['body-reviews'].forEach((review, index) => {
            // Add id if missing
            if (!review.id) {
              review.id = index + 1;
            }

            if (!review.dateValue && review.date) {
              // Parse the existing date string to Date object then convert to ISO
              const parsedDate = new Date(review.date);
              if (!isNaN(parsedDate.getTime())) {
                review.dateValue = this.formatDateToISO(parsedDate);
              } else {
                // Fallback to current date if parsing fails
                review.dateValue = this.formatDateToISO(new Date());
              }
            } else if (!review.dateValue) {
              // No existing date, use current date
              review.dateValue = this.formatDateToISO(new Date());
            }
            if (!review.images) {
              review.images = [];
            }
          });
          // Sort reviews by date (newest first)
          this.sortReviewsByDate();
          this.calculateRatingSummary();
          this.updateMaxRandomReviewsCount();
        }
      },
      deep: true,
    },
    selectedGender() {
      this.updateMaxRandomReviewsCount();
    },
    fields: {
      deep: true,
      handler(newVal) {
        if (newVal && newVal['body-reviews']) {
          this.calculateRatingSummary();
        }
      },
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .ck-editor__editable_inline {
  min-height: 100px !important;
}
::v-deep .ck-editor__editable {
  min-height: 100px !important;
}
.label-input {
  font-size: 14px;
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
  display: block;
}

.wrap-reviews-form {
  margin-top: 20px;
}

.header-modal,
.footer-modal {
  padding: 0 24px;
  border-bottom: 1px solid #e0e0e0;
}

.footer-modal {
  border-top: 1px solid #e0e0e0;
  border-bottom: none;
}

.content-modal {
  max-height: 70vh;
  overflow-y: auto;
}

.d-flex {
  display: flex;
}

.align-center {
  align-items: center;
}

.gap-3 {
  gap: 12px;
}

.reviewer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e0e0e0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: relative;
    z-index: 2;
  }

  &::before {
    content: '';
    font-size: 20px;
    color: #999;
    position: absolute;
  }
}

.reviewer-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.reviewer-name {
  font-size: 16px;
  font-weight: 500;
}

.review-images-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}

.review-image-item {
  position: relative;
}

.image-container {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  width: 90px;
  height: 90px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &:hover .image-overlay {
    opacity: 1;
  }
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;

  .edit-btn,
  .remove-btn {
    background-color: rgba(255, 255, 255, 0.2) !important;
  }
}

.no-images-placeholder {
  text-align: center;
  padding: 40px 20px;
  border: 2px dashed #ccc;
  border-radius: 8px;
  color: #666;
  margin-top: 16px;

  p {
    margin: 0;
    font-style: italic;
  }
}

.edit-review-dialog {
  .v-card__title {
    position: sticky;
    top: 0;
    background-color: #fff;
    z-index: 10;
    border-bottom: 1px solid #e0e0e0;
    padding: 16px 24px;
  }

  .v-card__actions {
    position: sticky;
    bottom: 0;
    background-color: #fff;
    z-index: 10;
    border-top: 1px solid #e0e0e0;
    padding: 16px 24px;
  }

  .v-card__text {
    max-height: 70vh;
    overflow-y: auto;
    padding: 24px;
  }
}

.reviews-nav {
  padding-block: 16px;
}

.reviews-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #e0e0e0;
}

.reviews-header-left {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selected-info {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  border-left: 4px solid #1976d2;
  width: fit-content;
  margin-left: auto;
}

.selected-count {
  font-size: 14px;
  font-weight: 500;
  color: #1976d2;
}

.reviews-controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.search-section {
  display: flex;
  align-items: center;
}

.import-section {
  display: flex;
  align-items: center;
}

.export-section {
  display: flex;
  align-items: center;
}

.upload-zone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  transition: 0.2s;
}

.upload-zone--active {
  border-color: #1976d2;
  background-color: #e3f2fd;
}

@media (max-width: 768px) {
  .reviews-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .reviews-header-left {
    width: 100%;
  }

  .selected-info {
    justify-content: space-between;
  }

  .reviews-controls {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .search-section,
  .import-section {
    width: 100%;
  }

  .search-section .v-text-field {
    width: 100% !important;
    min-width: 100% !important;
  }
}

.description-color-picker {
  display: flex;
  align-items: center;
  gap: 8px;
}

.description-color-picker__native {
  width: 40px;
  height: 40px;
}
</style>
